import {Dispatch, SetStateAction, useMemo, useState} from 'react'
import {selectTheme, useSelectSettings} from '../../store'
import {
  QuestionnaireContentListItem,
  QuestionnaireTempInputConfigOptions,
  QuestionnaireTempQuestion,
  QuestionnaireTempSection,
  TempQuestionnaireDisplayStrategyUpsertItem,
  UpsertDisplayRule,
} from '../../model'
import {AddIcon} from '../../lib/svg/icon/add'
import {
  QuestionnaireInputInitialValue,
  QuestionnaireQuestion,
  QuestionnaireInput,
  QuestionnaireInputHeight,
  QuestionnaireTextarea,
  DropdownMenu,
  PopupMoveQuestionnaireQuestion,
} from '..'
import {DeleteIcon} from '../../lib/svg/icon/delete'
import {DropdownIcon} from '../../lib/svg/icon/dropdown'
import {
  BetaType,
  IQuestionnaireDisplayLogicType,
  IQuestionnaireDisplayRuleConditionNumber,
  IQuestionnaireDisplayRuleConditionOption,
  IQuestionnaireDisplayRuleConditionType,
  IQuestionnaireDisplayRuleSourceQuestion,
  IQuestionnaireDisplayRuleTargetQuestion,
  IQuestionnaireDisplayRuleTargetSection,
  IQuestionnaireDisplayRuleTargetSubmit,
  IQuestionnaireDisplayRuleTargetType,
  IQuestionnaireQuestion,
  IQuestionnaireSection,
  QuestionnaireQuestionType,
} from '../../shared/db'
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable'
import {closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor} from '@dnd-kit/core'
import {isInBetaType, t} from '../../lib'
import {SkipIcon} from '../../lib/svg/icon/skip'
import {InvisibleIcon} from '../../lib/svg/icon/invisible'

export const QuestionnaireSectionV2 = (props: {
  key: string
  id: string
  index: number
  section: QuestionnaireTempSection
  editingBlock: string | null
  setEditingBlock: Dispatch<SetStateAction<string | null>>
  addQuestion: (sectionId: string, index?: number) => void
  viewOnly: boolean
  tempSectionList: (QuestionnaireTempSection | IQuestionnaireSection)[]
  setTempSectionList: Dispatch<SetStateAction<(QuestionnaireTempSection | IQuestionnaireSection)[]>>
  setRenderPopupReorderSection: Dispatch<SetStateAction<boolean>>
  setRenderPopupEditDisplayLogic: Dispatch<SetStateAction<boolean>>
  setDisplayLogicTargetType: Dispatch<SetStateAction<'section' | 'question'>>
  setEditingLogicItemId: Dispatch<SetStateAction<string>>
  upsertDisplayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[]
  setRenderPopupWarningDisplayStrategyForbiddenOperation: Dispatch<SetStateAction<boolean>>
}) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const {
    index,
    id,
    section,
    editingBlock,
    setEditingBlock,
    addQuestion,
    viewOnly,
    tempSectionList,
    setTempSectionList,
    setRenderPopupReorderSection,
    setRenderPopupEditDisplayLogic,
    setDisplayLogicTargetType,
    setEditingLogicItemId,
    upsertDisplayStrategyList,
    setRenderPopupWarningDisplayStrategyForbiddenOperation,
  } = props

  const currentDisplayStrategy = upsertDisplayStrategyList.find((item) => item.sectionId === id)
  const hasDisplayStrategy = useMemo(() => {
    if (!currentDisplayStrategy) return false
    return currentDisplayStrategy.displayStrategy.displayLogicList?.some(({displayRuleList}) => {
      if (displayRuleList && displayRuleList.length > 0) return true
    })
  }, [currentDisplayStrategy])

  const lockedByDisplayStrategy = useMemo(() => {
    if (hasDisplayStrategy) return true
    return upsertDisplayStrategyList.some(({displayStrategy}) => {
      return displayStrategy.displayLogicList?.some(({displayRuleList}) =>
        displayRuleList?.some((rule) => {
          const target = rule.target as
            | IQuestionnaireDisplayRuleTargetSection
            | IQuestionnaireDisplayRuleTargetQuestion
            | IQuestionnaireDisplayRuleTargetSubmit
          if (!target) return false
          return (
            target.type === IQuestionnaireDisplayRuleTargetType.Section && target.questionnaireSectionId === section.id
          )
        }),
      )
    })
  }, [upsertDisplayStrategyList])

  const questionIsLockedByDisplayStrategy = (questionId: string) => {
    return upsertDisplayStrategyList.some(({displayStrategy}) => {
      return displayStrategy.displayLogicList?.some(({displayRuleList}) => {
        return displayRuleList?.some((rule) => {
          const source = rule.source as IQuestionnaireDisplayRuleSourceQuestion
          const target = rule.target as
            | IQuestionnaireDisplayRuleTargetSection
            | IQuestionnaireDisplayRuleTargetQuestion
            | IQuestionnaireDisplayRuleTargetSubmit
          if (!target && !source) return false
          return (
            (target?.type === IQuestionnaireDisplayRuleTargetType.Question &&
              target?.questionnaireQuestionId === questionId) ||
            source?.questionnaireQuestionId === questionId
          )
        })
      })
    })
  }

  const hasLegendOrDescription = section.legend.length > 0 || section.description.length > 0
  const isEditing = !viewOnly && editingBlock === id
  const [dropped, setDropped] = useState(false)
  const questionList = useMemo(() => section.questionList ?? [], [section.questionList])
  const [movingQuestion, setMovingQuestion] = useState<QuestionnaireTempQuestion | null>(null)
  const settings = useSelectSettings()
  const handleEdit = (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (viewOnly) return
    e.stopPropagation()
    setEditingBlock(id)
  }

  const handleClickReorder = () => {
    setDropped(false)
    setRenderPopupReorderSection(true)
  }

  const handleChangeSectionLegend = (value: string) => {
    const newTempSectionList = [...tempSectionList]
    newTempSectionList[index] = {...section, legend: value}
    setTempSectionList(newTempSectionList)
  }

  const handleChangeSectionDescription = (value: string) => {
    const newTempSectionList = [...tempSectionList]
    newTempSectionList[index] = {...section, description: value}
    setTempSectionList(newTempSectionList)
  }

  const handleRemoveSectionTitleAndDescription = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newTempSectionList = [...tempSectionList]
    newTempSectionList[index] = {...section, legend: '', description: ''}
    setTempSectionList(newTempSectionList)
    setEditingBlock(null)
  }

  const handleAddQuestion = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    addQuestion(id)
  }

  const handleDeleteSection = () => {
    if (lockedByDisplayStrategy) {
      setRenderPopupWarningDisplayStrategyForbiddenOperation(true)
      return
    }
    if (tempSectionList.length === 1) return
    const newTempSectionList = [...tempSectionList]
    newTempSectionList.splice(index, 1)
    setTempSectionList(newTempSectionList.map((s, i) => ({...s, index: i})))
    setEditingBlock(null)
  }

  const onDragEnd = ({active, over}: DragEndEvent) => {
    if (!over) return
    if (active.id === over.id) return
    if (questionIsLockedByDisplayStrategy(active.id as string)) {
      return setRenderPopupWarningDisplayStrategyForbiddenOperation(true)
    }
    const oldIndex = questionList.findIndex(({id}) => id === active.id)
    const newIndex = questionList.findIndex(({id}) => id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const newQuestionList = arrayMove([...questionList], oldIndex, newIndex)
    const newTempSectionList = [...tempSectionList]
    newTempSectionList[index] = {...section, questionList: newQuestionList}
    setTempSectionList(newTempSectionList.map((s, i) => ({...s, index: i})))
  }

  const handleEditLogic = (targetType?: 'section' | 'question', targetId?: string) => {
    setRenderPopupEditDisplayLogic(true)
    setDisplayLogicTargetType(targetType ?? 'section')
    setEditingLogicItemId(targetId ?? id)
  }

  const contentList: {
    type: string
    item: QuestionnaireContentListItem
  }[] = useMemo(() => {
    return tempSectionList.flatMap((section) => [
      {
        type: 'section',
        item: section,
      },
      ...(section.questionList?.map((question) => ({
        type: 'question',
        item: question,
      })) ?? []),
    ])
  }, [tempSectionList])

  const generateRuleText = (rule: UpsertDisplayRule, type: IQuestionnaireDisplayLogicType) => {
    switch (type) {
      case IQuestionnaireDisplayLogicType.Skip: {
        const ruleTarget = rule.target as
          | IQuestionnaireDisplayRuleTargetSection
          | IQuestionnaireDisplayRuleTargetQuestion
          | IQuestionnaireDisplayRuleTargetSubmit
        if (ruleTarget.type === IQuestionnaireDisplayRuleTargetType.Submit) {
          return 'After this section: Submit the questionnaire'
        } else {
          const targetItem = contentList.find(({item}) =>
            ruleTarget.type === IQuestionnaireDisplayRuleTargetType.Section
              ? item.id === ruleTarget.questionnaireSectionId
              : item.id === ruleTarget.questionnaireQuestionId,
          )
          if (targetItem?.type === 'section') {
            return `After this section: ${(targetItem.item as IQuestionnaireSection | QuestionnaireTempSection).legend}`
          } else if (targetItem?.type === 'question') {
            return `After this section: ${
              (targetItem.item as IQuestionnaireQuestion | QuestionnaireTempQuestion).description
            }`
          }
        }
        break
      }
      case IQuestionnaireDisplayLogicType.Display: {
        const sourceQuestion = contentList.find(
          ({item}) => item.id === (rule.source as IQuestionnaireDisplayRuleSourceQuestion)?.questionnaireQuestionId,
        )
        if (!sourceQuestion) return ''
        const typedSourceQuestion = sourceQuestion.item as QuestionnaireTempQuestion | IQuestionnaireQuestion
        const itemIndex = contentList.findIndex(({item}) => item.id === typedSourceQuestion.id)
        if (itemIndex === -1) return ''
        let sectionIndex = 0
        let questionIndex = 0
        for (let i = 0; i <= itemIndex; i++) {
          if (contentList[i].type === 'section') {
            sectionIndex += 1
            questionIndex = 0
          } else {
            questionIndex += 1
          }
        }
        const ruleCondition = rule.condition as
          | IQuestionnaireDisplayRuleConditionOption
          | IQuestionnaireDisplayRuleConditionNumber
        if (ruleCondition.type === IQuestionnaireDisplayRuleConditionType.Option) {
          const optionName =
            (typedSourceQuestion.inputConfig as QuestionnaireTempInputConfigOptions).options?.find(({value, id}) =>
              [value, id].includes(ruleCondition.inputOptionList?.[0].optionId),
            )?.name ?? ''
          return `Hide this section unless S${sectionIndex}-Q${questionIndex}. ${typedSourceQuestion.description} [${optionName}] is selected`
        }
        if (rule.condition?.inputNumberList && rule.condition.inputNumberList.length > 1) {
          const numberMin = Math.min(ruleCondition.inputNumberList[0].value, ruleCondition.inputNumberList[1].value)
          const numberMax = Math.max(ruleCondition.inputNumberList[0].value, ruleCondition.inputNumberList[1].value)
          return `Hide this section unless S${sectionIndex}-Q${questionIndex}. ${typedSourceQuestion.description} value between ${numberMin} and ${numberMax}`
        }
        return `Hide this section unless S${sectionIndex}-Q${questionIndex}. ${typedSourceQuestion.description} value ${
          ruleCondition.inputNumberList?.[0].operator ?? '='
        } ${ruleCondition.inputNumberList?.[0].value ?? 0}`
      }
      default:
        return ''
    }
  }

  return (
    <>
      {movingQuestion && (
        <PopupMoveQuestionnaireQuestion
          {...{
            tempSectionList,
            setTempSectionList,
            sectionId: id,
            movingQuestion,
            setMovingQuestion,
          }}
        />
      )}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}>
        <div
          css={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          {/* index tag */}
          <div
            css={{
              height: '34px',
              borderTopRightRadius: '16px',
              borderTopLeftRadius: '16px',
              backgroundColor: color.black,
              color: color.white,
              fontWeight: fontWeight.thick,
              textAlign: 'center',
              padding: '8px 16px',
            }}>
            Section {index + 1} / {tempSectionList.length}
          </div>
          {/* edit logic button and dropdown menu */}
          {!viewOnly && (
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}>
              <button
                onClick={() => handleEditLogic()}
                css={{
                  padding: '3px 6px',
                  border: `1px solid ${color.border.default}`,
                  borderRadius: '3px',
                  color: color.textIcon.secondary,
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: fontSize.h7,
                  fontWeight: fontWeight.bold,
                }}>
                Edit logic
              </button>
              <div
                css={{
                  position: 'relative',
                }}>
                <button
                  onClick={() => setDropped(!dropped)}
                  css={{
                    height: '24px',
                    width: '24px',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    ':hover': {backgroundColor: color.surface.hover.hoverWhiteBg},
                  }}>
                  <DropdownIcon
                    {...{
                      width: 20,
                      height: 20,
                    }}
                  />
                </button>
                <DropdownMenu
                  {...{
                    dropped,
                    options: [
                      {text: 'Reorder', onClick: handleClickReorder},
                      {text: 'Delete', textColor: color.textIcon.warning, onClick: handleDeleteSection},
                    ],
                    parentCSS: {top: '24px', right: 0},
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div
          css={{
            width: '100%',
            borderTop: `3px solid ${color.black}`,
            boxShadow: '0 4px 12px 0 rgba(212, 212, 212, 0.25)',
            backgroundColor: color.surface.white,
          }}>
          {hasDisplayStrategy && (
            <div
              css={{
                width: '100%',
                padding: '16px',
                borderBottom: `1px dashed ${color.border.black}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
              {currentDisplayStrategy?.displayStrategy.displayLogicList?.flatMap(({type, displayRuleList}) =>
                displayRuleList?.map((rule) => (
                  <div
                    key={t.uuid()}
                    css={{
                      width: '100%',
                      padding: '8px 16px',
                      borderRadius: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor:
                        type === IQuestionnaireDisplayLogicType.Display ? color.surface.nested : color.tag.purple.light,
                    }}>
                    {type === IQuestionnaireDisplayLogicType.Display ? (
                      <InvisibleIcon width={14} fill={color.textIcon.secondary} />
                    ) : (
                      <SkipIcon width={14} fill={color.tag.purple.dark} />
                    )}
                    <span
                      css={{
                        color:
                          type === IQuestionnaireDisplayLogicType.Display
                            ? color.textIcon.secondary
                            : color.tag.purple.dark,
                        fontSize: fontSize.h7,
                        fontWeight: fontWeight.bold,
                      }}>
                      {generateRuleText(rule, type)}
                    </span>
                  </div>
                )),
              )}
            </div>
          )}
          <div
            css={{
              width: '100%',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
            {/* empty state for legend field */}
            {!viewOnly && !hasLegendOrDescription && !isEditing && (
              <button
                onClick={handleEdit}
                css={{
                  width: '100%',
                  height: '24px',
                  border: `1px solid ${color.border.default}`,
                  borderRadius: '3px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                }}>
                <AddIcon fill={color.textIcon.secondary} width={16} height={16} />
                <span css={{fontSize: fontSize.h7, fontWeight: fontWeight.bold, color: color.textIcon.secondary}}>
                  Add Title And Description Page
                </span>
              </button>
            )}
            {/* render legend field when isEditing */}
            {!viewOnly && isEditing && (
              <div
                onClick={handleEdit}
                css={{
                  width: '100%',
                  padding: '16px 24px',
                  borderRadius: '5px',
                  border: `2px solid ${color.border.blue}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                <QuestionnaireInput
                  placeholder={QuestionnaireInputInitialValue.SectionTitle}
                  onChangeAction={handleChangeSectionLegend}
                  defaultValue={section.legend}
                  maxLength={50}
                  questionnaireInputHeight={QuestionnaireInputHeight.big}
                />
                <QuestionnaireTextarea
                  defaultValue={section?.description}
                  onChangeAction={handleChangeSectionDescription}
                  placeholder={QuestionnaireInputInitialValue.SectionDescription}
                  maxLength={250}
                  rows={4}
                />
                <div
                  css={{
                    marginTop: '8px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <button
                    onClick={handleRemoveSectionTitleAndDescription}
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      padding: '0 12px 0 8px',
                    }}>
                    <DeleteIcon width={16} height={16} />
                    <span css={{fontWeight: fontWeight.thick, color: color.textIcon.link}}>
                      Remove Section Title And Description
                    </span>
                  </button>
                  {/* <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <TextLink onClick={handleClickCancel}>Cancel</TextLink>
                    <Button onClick={handleClickSave}>Save</Button>
                  </div> */}
                </div>
              </div>
            )}
            {/* render legend field when is not editing */}
            {hasLegendOrDescription && !isEditing && (
              <div
                onClick={handleEdit}
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '16px 24px 32px',
                  borderBottom: `1px solid ${color.border.default}`,
                }}>
                <span
                  css={{
                    fontSize: fontSize.h3,
                    fontWeight: fontWeight.bold,
                  }}>
                  {section.legend}
                </span>
                <span>{section.description}</span>
              </div>
            )}
            {/* add question button */}
            {!viewOnly && questionList.length === 0 && (
              <button
                onClick={handleAddQuestion}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px 4px 4px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  border: 'none',
                  backgroundColor: color.white,
                  width: 'fit-content',
                  ':hover': {
                    backgroundColor: color.surface.hover.hoverWhiteBg,
                  },
                }}>
                <AddIcon fill={color.textIcon.link} width={12} height={12} />
                <span
                  css={{
                    fontSize: fontSize.h7,
                    fontWeight: fontWeight.bold,
                    color: color.textIcon.link,
                  }}>
                  Add Question
                </span>
              </button>
            )}
            <DndContext sensors={[useSensor(PointerSensor)]} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={questionList} strategy={verticalListSortingStrategy}>
                {questionList.map((question, index) => (
                  <QuestionnaireQuestion
                    {...{
                      key: question.id,
                      id: question.id,
                      index,
                      sectionId: id,
                      question,
                      tempSectionList,
                      setTempSectionList,
                      questionList,
                      editingBlock,
                      setEditingBlock,
                      viewOnly,
                      addQuestion,
                      setMovingQuestion,
                      handleEditLogic,
                      upsertDisplayStrategyList,
                      setRenderPopupWarningDisplayStrategyForbiddenOperation,
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          {hasDisplayStrategy && (
            <div
              css={{
                width: '100%',
                padding: '16px',
                borderTop: `1px dashed ${color.border.black}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
              {currentDisplayStrategy?.displayStrategy.displayLogicList
                ?.filter(({type}) => type === IQuestionnaireDisplayLogicType.Skip)
                .flatMap(({type, displayRuleList}) =>
                  displayRuleList?.map((rule) => (
                    <div
                      key={t.uuid()}
                      css={{
                        width: '100%',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: color.tag.purple.light,
                      }}>
                      <SkipIcon width={14} fill={color.tag.purple.dark} />
                      <span
                        css={{
                          color: color.tag.purple.dark,
                          fontSize: fontSize.h7,
                          fontWeight: fontWeight.bold,
                        }}>
                        {generateRuleText(rule, type)}
                      </span>
                    </div>
                  )),
                )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
